import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Plus, Pencil, PowerOff, Truck } from "lucide-react";

const EMPTY = {
  legal_name: "",
  trade_name: "",
  contact_name: "",
  phone: "",
  email: "",
  rfc: "",
  address: "",
  notes: "",
  active: true,
};

export default function Proveedores() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId } = useTenant();
  const canQuery = !isSuperAdmin || !!activeTenantId;
  const canCreate = hasPermission("suppliers:create");
  const canEdit = hasPermission("suppliers:edit");
  const canDelete = hasPermission("suppliers:delete");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};

  async function fetchList() {
    if (!canQuery) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers", {
        params: { ...tenantParam, search: search || undefined, include_inactive: includeInactive },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(fetchList, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, includeInactive, activeTenantId, isSuperAdmin]);

  function openCreate() { setEditing(null); setForm(EMPTY); setSheetOpen(true); }
  function openEdit(s) { setEditing(s); setForm({ ...EMPTY, ...s }); setSheetOpen(true); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        legal_name: form.legal_name?.trim(),
        trade_name: form.trade_name?.trim() || null,
        contact_name: form.contact_name?.trim() || null,
        phone: form.phone?.trim() || null,
        email: form.email?.trim() || null,
        rfc: form.rfc?.trim() ? form.rfc.trim().toUpperCase() : null,
        address: form.address?.trim() || null,
        notes: form.notes?.trim() || null,
        active: !!form.active,
      };
      if (editing) {
        await api.patch(`/suppliers/${editing.id}`, payload);
        toast.success("Proveedor actualizado");
      } else {
        await api.post("/suppliers", payload, { params: tenantParam });
        toast.success("Proveedor creado");
      }
      setSheetOpen(false);
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function deactivate(sup) {
    if (!confirm(`¿Desactivar el proveedor "${sup.legal_name}"?`)) return;
    try {
      const { data } = await api.delete(`/suppliers/${sup.id}`);
      toast.success(data?.had_purchase_orders
        ? "Proveedor desactivado (tiene OCs vinculadas — no se elimina físicamente)"
        : "Proveedor desactivado");
      fetchList();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="page-proveedores">
      <PageHeader
        title="Proveedores"
        description="Catálogo global de proveedores del tenant. Cada orden de compra debe apuntar a uno."
        actions={
          canCreate && (
            <Button className="btn-tenant" onClick={openCreate} disabled={!canQuery} data-testid="btn-new-supplier">
              <Plus className="h-4 w-4 mr-2" /> Nuevo proveedor
            </Button>
          )
        }
      />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Razón social, RFC, contacto…"
            className="pl-9 h-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-suppliers"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            checked={includeInactive}
            onCheckedChange={setIncludeInactive}
            data-testid="toggle-include-inactive"
          />
          Incluir inactivos
        </label>
        <div className="text-xs text-muted-foreground ml-auto" data-testid="suppliers-count">
          {items.length} proveedor{items.length === 1 ? "" : "es"}
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState
            title={canQuery ? "Sin proveedores" : "Selecciona un tenant"}
            description="Crea el primer proveedor para poder registrar órdenes de compra."
            icon={Truck}
            action={canCreate && canQuery && (
              <Button className="btn-tenant" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-2" /> Crear proveedor
              </Button>
            )}
          />
        ) : (
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Razón social</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>RFC</TableHead>
                <TableHead>Estatus</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id} className="tbl-row" data-testid={`row-supplier-${s.id}`}>
                  <TableCell>
                    <div className="font-medium">{s.legal_name}</div>
                    {s.trade_name && <div className="text-xs text-muted-foreground">{s.trade_name}</div>}
                  </TableCell>
                  <TableCell className="text-sm">{s.contact_name || "—"}</TableCell>
                  <TableCell className="text-sm mono">{s.phone || "—"}</TableCell>
                  <TableCell className="text-xs mono">{s.rfc || "—"}</TableCell>
                  <TableCell>
                    <Badge className={s.active ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                      {s.active ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`btn-edit-supplier-${s.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && s.active && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deactivate(s)}
                        data-testid={`btn-deactivate-supplier-${s.id}`}
                      >
                        <PowerOff className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="sheet-supplier">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar proveedor" : "Nuevo proveedor"}</SheetTitle>
            <SheetDescription>
              Datos básicos del proveedor. La razón social es obligatoria.
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={save} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Razón social *</Label>
              <Input
                required
                value={form.legal_name}
                onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                data-testid="sup-legal-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre comercial</Label>
              <Input
                value={form.trade_name || ""}
                onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
                data-testid="sup-trade-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contacto</Label>
                <Input
                  value={form.contact_name || ""}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  data-testid="sup-contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="sup-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email || ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="sup-email"
                />
              </div>
              <div className="space-y-2">
                <Label>RFC</Label>
                <Input
                  value={form.rfc || ""}
                  onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                  className="mono uppercase"
                  data-testid="sup-rfc"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Textarea
                rows={2}
                value={form.address || ""}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                data-testid="sup-address"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                rows={2}
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="sup-notes"
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-border">
              <div>
                <Label>Activo</Label>
                <p className="text-xs text-muted-foreground">Al desactivar, no aparece en el selector de OC nuevas.</p>
              </div>
              <Switch
                checked={!!form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="sup-active"
              />
            </div>
            <SheetFooter className="sticky bottom-0 bg-white pt-3 -mx-6 px-6 pb-1 border-t border-border">
              <Button type="button" variant="outline" onClick={() => setSheetOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="btn-tenant" data-testid="sup-save">
                {saving ? "Guardando…" : editing ? "Guardar" : "Crear proveedor"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}
